<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import type { PropType } from "vue"
import { useI18n } from "vue-i18n"
import { ColumnConfig } from "@types"
import type { FolderForDialog } from "@types"
import { validateFolders } from "./folderDialogValidation"

// ================================
// PROPS & EMITS
// ================================
const props = defineProps({
    /** Autorise la sélection de plusieurs dossiers (false = un seul) */
    multiple: { type: Boolean, default: false },
    /** Arbre des dossiers à afficher (format FolderForDialog) */
    folders: {
        type: Array as PropType<FolderForDialog[]>,
        required: true,
        validator: function (value: unknown) {
            return validateFolders(value)
        },
    },
    /** Affiche l'état de chargement du tableau */
    loading: { type: Boolean, default: false },
})

const emit = defineEmits<{
    /** Émis avec l'id du dossier (mode single) ou la liste des ids (mode multiple) */
    "select:folder": [payload: string | string[]]
}>()

// ================================
// ÉTAT
// ================================
const [visible] = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

const selectedFolder = ref<
    Array<{
        key: string
        folder: string
        data?: {
            _id_folder: string
            folder: string
            building: number
        }
        children?: unknown[]
    }>
>([])

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            component: {
                folder_dialog: {
                    title: "Sélectionner un dossier",
                    title_multiple: "Sélectionner des dossiers",
                    folder: "Dossier",
                    buildings: "Bâtiments",
                    select: "Sélectionner",
                    cancel: "Annuler",
                    no_folder_selected: "Veuillez sélectionner un dossier",
                    no_folder: "Aucun dossier disponible",
                },
            },
        },
    },
})

const dialogTitle = computed(() =>
    props.multiple
        ? t("component.folder_dialog.title_multiple")
        : t("component.folder_dialog.title")
)

// ================================
// DONNÉES CALCULÉES
// ================================
function transformFolderTreeToTreeNodes(folderTree: FolderForDialog[]): Array<{
    key: string
    folder: string
    data: {
        _id_folder: string
        folder: string
        building: number
    }
    children: unknown[]
}> {
    return folderTree.map((folder) => {
        return {
            key: folder._id,
            folder: folder.name,
            data: {
                _id_folder: folder._id,
                folder: folder.name,
                building: folder.buildings ? folder.buildings.length : 0,
            },
            children:
                folder.children && folder.children.length > 0
                    ? transformFolderTreeToTreeNodes(folder.children)
                    : [],
        }
    })
}

const tableData = computed(() => {
    if (!props.folders || props.folders.length === 0) {
        return []
    }
    return transformFolderTreeToTreeNodes(props.folders)
})

const columns = computed<ColumnConfig[]>(() => [
    {
        field: "folder",
        header: t("component.folder_dialog.folder"),
        link: true,
        sortable: true,
        style: { width: "20rem" },
        expander: true,
    },
    {
        field: "building",
        header: t("component.folder_dialog.buildings"),
        sortable: true,
        style: { width: "10rem" },
        expander: false,
    },
])

// ================================
// INTERACTIONS
// ================================
function onSelectFolder() {
    const selected = selectedFolder.value
    if (!selected || selected.length === 0) return

    if (props.multiple) {
        const folderIds = selected
            .map((node) => node.data?._id_folder || node.key)
            .filter(Boolean)
        if (folderIds.length > 0) {
            emit("select:folder", folderIds)
            visible.value = false
            selectedFolder.value = []
        }
    } else {
        const node = selected[0]
        const folderId = node?.data?._id_folder || node?.key
        if (folderId) {
            emit("select:folder", folderId)
            visible.value = false
            selectedFolder.value = []
        }
    }
}

function onCancel() {
    visible.value = false
    selectedFolder.value = []
}
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :style="{ width: '50vw' }"
        :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
        :header="dialogTitle"
    >
        <BaseTableTree
            v-model:selection="selectedFolder"
            :value="tableData"
            :columns="columns"
            data-key="key"
            :selection-mode="multiple ? 'checkbox' : 'single'"
            :loading="loading"
            :empty-message="t('component.folder_dialog.no_folder')"
            scrollable
            search
            :size="'small'"
            selectable
        />
        <template #footer>
            <div class="flex justify-end gap-2">
                <BaseButton
                    :label="t('component.folder_dialog.cancel')"
                    variant="text"
                    @click="onCancel"
                />
                <BaseButton
                    :label="t('component.folder_dialog.select')"
                    :disabled="!selectedFolder || selectedFolder.length === 0"
                    @click="onSelectFolder"
                />
            </div>
        </template>
    </BaseDialog>
</template>
